import type { CatalogItemDto, WarehouseDto } from '@bqcrm/shared';
import { Select, type SelectProps } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../api';
import { useWarehouses } from '../../data';

export const whLabel = (w: WarehouseDto) => `${w.companyCode} · ${w.code} — ${w.name}`;

/** Wybór magazynu spośród dostępnych pracownikowi (firmy × magazyny — filtr na serwerze). */
export function WarehouseSelect({ value, onChange, companyId, exclude, ...rest }: { value: string | null; onChange: (id: string | null) => void; companyId?: string | null; exclude?: string | null } & Omit<SelectProps, 'value' | 'onChange' | 'data'>) {
  const whs = useWarehouses();
  const data = (whs.data ?? [])
    .filter((w) => w.active && (!companyId || w.companyId === companyId) && w.id !== exclude)
    .map((w) => ({ value: w.id, label: whLabel(w) }));
  return <Select data={data} value={value} onChange={onChange} allowDeselect={false} {...rest} />;
}

/** Produkty magazynowe (wyszukiwanie: nazwa, indeks, EAN). */
export function useStockItems() {
  return useQuery({ queryKey: ['catalog', 'stocked'], queryFn: () => api<CatalogItemDto[]>('/catalog?stocked=true&active=true'), staleTime: 60_000 });
}

export function ItemSelect({ value, onChange, items, ...rest }: { value: string | null; onChange: (id: string | null) => void; items: CatalogItemDto[] } & Omit<SelectProps, 'value' | 'onChange' | 'data'>) {
  const data = items.map((i) => ({ value: i.id, label: `${i.name}${i.code ? ` (${i.code})` : ''}${i.ean ? ` · ${i.ean}` : ''}` }));
  return <Select data={data} value={value} onChange={onChange} searchable limit={50} nothingFoundMessage="Nie znaleziono produktu" {...rest} />;
}

/** Sprawa do wydania na realizację — po numerze lub tytule, tylko sprawy firm magazyniera (bez danych klienta). */
export function CaseSelect({ value, label, onChange }: { value: string | null; label: string | null; onChange: (id: string | null, label: string | null) => void }) {
  const [text, setText] = useState('');
  const [term] = useDebouncedValue(text.trim(), 300);
  const q = useQuery({ queryKey: ['stock-cases', term], queryFn: () => api<{ id: string; number: string; title: string }[]>(`/stock/cases?q=${encodeURIComponent(term)}`), enabled: term.length >= 2 });
  const data = (q.data ?? []).map((c) => ({ value: c.id, label: `${c.number} — ${c.title}` }));
  return (
    <Select
      label="Sprawa (realizacja)"
      placeholder={label ?? 'Wpisz numer sprawy'}
      searchable
      clearable
      data={value && label && !data.some((d) => d.value === value) ? [{ value, label }, ...data] : data}
      value={value}
      searchValue={text}
      onSearchChange={setText}
      onChange={(v) => onChange(v, data.find((d) => d.value === v)?.label ?? null)}
      nothingFoundMessage={term.length >= 2 ? 'Nie znaleziono sprawy' : 'Wpisz co najmniej 2 znaki'}
      filter={({ options }) => options}
    />
  );
}

export type Recipient = { id: string; name: string; leaderId: string | null; members: { id: string; name: string }[] };

/** Ekipy i ich dzisiejszy skład — odbiorcy wydań. */
export function useRecipients() {
  return useQuery({ queryKey: ['stock-recipients'], queryFn: () => api<Recipient[]>('/stock/recipients'), staleTime: 300_000 });
}
